import { useState, useEffect, useRef } from "react";
import Navigation from "../components/Navigation";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Settings, User, Phone, Mail, Trophy, Users, Shield, Building, Globe, Bell, Calendar, Save, Loader2, Camera, Upload } from "lucide-react";
import UserAvatar from "../components/UserAvatar";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserSettings({ user, onLogout }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [avatarKey, setAvatarKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/settings`, { headers: { Authorization: `Bearer ${token}` } });
        setSettings(res.data);
      } catch (err) {
        console.error("Failed to load settings:", err);
        // Create default settings so the page still renders
        setSettings({
          user_id: "",
          name: "",
          email: "",
          phone: "",
          sport: "",
          position: "",
          team_name: "",
          team_role: "",
          coach_name: "",
          sport_consultant: "",
          organization: "",
          timezone: "",
          notification_email: true,
          notification_sms: false,
          calendar_active: true,
        });
        toast.error("Failed to load settings. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.put(`${API}/settings`, settings, { headers: { Authorization: `Bearer ${token}` } });
      setSettings(res.data);
      toast.success("Settings saved successfully");
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/settings/profile-picture`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setAvatarKey((k) => k + 1);
      toast.success("Profile picture updated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload picture");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        <Navigation user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <Navigation user={user} onLogout={onLogout} />
      <div className="max-w-3xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 data-testid="settings-title" className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Settings className="w-7 h-7 text-emerald-600" />
              User Settings
            </h1>
            <p className="text-sm text-gray-500 mt-1">Manage your profile and preferences</p>
          </div>
          <Button data-testid="save-settings-btn" onClick={handleSave} disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </div>

        <div className="space-y-4">
          {/* Profile Picture */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-5">
                <div className="relative group">
                  <UserAvatar key={avatarKey} userId={user?.id} size="xl" />
                  <button
                    data-testid="avatar-upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-all cursor-pointer"
                  >
                    {uploading ? (
                      <Loader2 className="w-6 h-6 text-white animate-spin opacity-0 group-hover:opacity-100 transition-opacity" />
                    ) : (
                      <Camera className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    data-testid="avatar-file-input"
                  />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-800">{settings.name || "Your Name"}</h3>
                  <p className="text-xs text-gray-500">{settings.email || "your@email.com"}</p>
                  <Button
                    data-testid="change-photo-btn"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="mt-2 text-xs h-7 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  >
                    {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                    {uploading ? "Uploading..." : "Change Photo"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Personal Information */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold flex items-center gap-2">
                <User className="w-5 h-5 text-emerald-600" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-xs font-semibold text-gray-600">Full Name</Label>
                  <Input data-testid="settings-name" id="name" value={settings.name || ""} onChange={e => handleChange("name", e.target.value)} placeholder="Your full name" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-xs font-semibold text-gray-600">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input data-testid="settings-email" id="email" type="email" value={settings.email || ""} onChange={e => handleChange("email", e.target.value)} className="pl-10" placeholder="your@email.com" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone" className="text-xs font-semibold text-gray-600">Phone Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input data-testid="settings-phone" id="phone" value={settings.phone || ""} onChange={e => handleChange("phone", e.target.value)} className="pl-10" placeholder="+1 (555) 000-0000" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="timezone" className="text-xs font-semibold text-gray-600">Timezone</Label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input data-testid="settings-timezone" id="timezone" value={settings.timezone || ""} onChange={e => handleChange("timezone", e.target.value)} className="pl-10" placeholder="e.g. America/New_York" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sport & Team */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-600" />
                Sport & Team
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="sport" className="text-xs font-semibold text-gray-600">Sport</Label>
                  <Input data-testid="settings-sport" id="sport" value={settings.sport || ""} onChange={e => handleChange("sport", e.target.value)} placeholder="e.g. Basketball, Soccer" />
                </div>
                <div>
                  <Label htmlFor="position" className="text-xs font-semibold text-gray-600">Position</Label>
                  <Input data-testid="settings-position" id="position" value={settings.position || ""} onChange={e => handleChange("position", e.target.value)} placeholder="e.g. Point Guard, Striker" />
                </div>
                <div>
                  <Label htmlFor="team_name" className="text-xs font-semibold text-gray-600">Team Name</Label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input data-testid="settings-team" id="team_name" value={settings.team_name || ""} onChange={e => handleChange("team_name", e.target.value)} className="pl-10" placeholder="Your team name" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="team_role" className="text-xs font-semibold text-gray-600">Team Role</Label>
                  <Input data-testid="settings-team-role" id="team_role" value={settings.team_role || ""} onChange={e => handleChange("team_role", e.target.value)} placeholder="e.g. Captain, Starter" />
                </div>
                <div>
                  <Label htmlFor="organization" className="text-xs font-semibold text-gray-600">Organization</Label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input data-testid="settings-org" id="organization" value={settings.organization || ""} onChange={e => handleChange("organization", e.target.value)} className="pl-10" placeholder="School, Club, or Organization" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Coaching & Consultant */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                Coaching & Consultant
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="coach_name" className="text-xs font-semibold text-gray-600">Coach Name</Label>
                  <Input data-testid="settings-coach" id="coach_name" value={settings.coach_name || ""} onChange={e => handleChange("coach_name", e.target.value)} placeholder="Your head coach" />
                </div>
                <div>
                  <Label htmlFor="sport_consultant" className="text-xs font-semibold text-gray-600">Sport Consultant</Label>
                  <Input data-testid="settings-consultant" id="sport_consultant" value={settings.sport_consultant || ""} onChange={e => handleChange("sport_consultant", e.target.value)} placeholder="Sport psychology consultant (if applicable)" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notifications & Preferences */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold flex items-center gap-2">
                <Bell className="w-5 h-5 text-violet-600" />
                Notifications & Preferences
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-800">Email Notifications</p>
                  <p className="text-xs text-gray-500">Receive reminders and reports via email</p>
                </div>
                <Switch data-testid="settings-notif-email" checked={settings.notification_email ?? true} onCheckedChange={v => handleChange("notification_email", v)} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-800">SMS Notifications</p>
                  <p className="text-xs text-gray-500">Receive text message alerts</p>
                </div>
                <Switch data-testid="settings-notif-sms" checked={settings.notification_sms ?? false} onCheckedChange={v => handleChange("notification_sms", v)} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-800">Calendar Active</p>
                  <p className="text-xs text-gray-500">Show upcoming events on dashboard</p>
                </div>
                <Switch data-testid="settings-calendar" checked={settings.calendar_active ?? true} onCheckedChange={v => handleChange("calendar_active", v)} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Save button at bottom */}
        <div className="mt-6 flex justify-end">
          <Button data-testid="save-settings-bottom-btn" onClick={handleSave} disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
}
